import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Notice, NoticeInput, NoticeUpdateInput, NoticeFilterOptions } from '../types/notice.types';

const NOTICE_COLLECTION = 'notices';
const noticesRef = collection(db, NOTICE_COLLECTION);

/**
 * 모든 공지사항 조회
 */
export async function getAllNotices(options: NoticeFilterOptions = {}): Promise<Notice[]> {
  const { activeOnly = false, displayType, sortOrder = 'asc' } = options;

  try {
    const snapshot = await getDocs(noticesRef);

    let notices: Notice[] = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Notice[];

    if (activeOnly) {
      notices = notices.filter((n) => n.isActive === true);
    }

    if (displayType) {
      notices = notices.filter((n) => n.displayType === displayType);
    }

    // 고정 공지 우선, order 기준 정렬
    notices.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
    });

    return notices;
  } catch (error) {
    console.error('[noticeService] getAllNotices 실패:', error);
    throw error;
  }
}

/**
 * 단일 공지사항 조회
 */
export async function getNoticeById(id: string): Promise<Notice | null> {
  try {
    const docRef = doc(db, NOTICE_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    return { id: docSnap.id, ...docSnap.data() } as Notice;
  } catch (error) {
    console.error('[noticeService] getNoticeById 실패:', error);
    throw error;
  }
}

/**
 * 공지사항 생성
 */
export async function createNotice(input: NoticeInput): Promise<Notice> {
  try {
    const now = Timestamp.now();
    const newNotice = {
      title: input.title,
      body: input.body,
      imageUrl: input.imageUrl || '',
      linkUrl: input.linkUrl || '',
      linkLabel: input.linkLabel || '자세히 보기',
      priority: input.priority,
      displayType: input.displayType,
      category: input.category,
      isActive: input.isActive ?? true,
      isPinned: input.isPinned ?? false,
      startAt: input.startAt ?? null,
      endAt: input.endAt ?? null,
      order: input.order,
      createdBy: input.createdBy || '',
      created_at: now,
      updated_at: now,
    };

    const docRef = await addDoc(noticesRef, newNotice);
    return { id: docRef.id, ...newNotice };
  } catch (error) {
    console.error('[noticeService] createNotice 실패:', error);
    throw error;
  }
}

/**
 * 공지사항 수정
 */
export async function updateNotice(id: string, input: NoticeUpdateInput): Promise<Notice> {
  try {
    const docRef = doc(db, NOTICE_COLLECTION, id);
    const updateData = {
      ...input,
      updated_at: Timestamp.now(),
    };

    await updateDoc(docRef, updateData);

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() } as Notice;
  } catch (error) {
    console.error('[noticeService] updateNotice 실패:', error);
    throw error;
  }
}

/**
 * 공지사항 삭제
 */
export async function deleteNotice(id: string): Promise<void> {
  try {
    const docRef = doc(db, NOTICE_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('[noticeService] deleteNotice 실패:', error);
    throw error;
  }
}

/**
 * 공지사항 활성화 토글
 */
export async function toggleNoticeActive(id: string, isActive: boolean): Promise<void> {
  try {
    const docRef = doc(db, NOTICE_COLLECTION, id);
    await updateDoc(docRef, { isActive, updated_at: Timestamp.now() });
  } catch (error) {
    console.error('[noticeService] toggleNoticeActive 실패:', error);
    throw error;
  }
}

/**
 * 공지사항 순서 일괄 업데이트
 */
export async function updateNoticeOrders(updates: { id: string; order: number }[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    const now = Timestamp.now();

    updates.forEach(({ id, order }) => {
      const docRef = doc(db, NOTICE_COLLECTION, id);
      batch.update(docRef, { order, updated_at: now });
    });

    await batch.commit();
  } catch (error) {
    console.error('[noticeService] updateNoticeOrders 실패:', error);
    throw error;
  }
}

/**
 * 다음 순서 번호 가져오기
 */
export async function getNextNoticeOrder(): Promise<number> {
  try {
    const q = query(noticesRef, orderBy('order', 'desc'));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return 1;

    const maxOrder = snapshot.docs[0].data().order || 0;
    return maxOrder + 1;
  } catch (error) {
    console.error('[noticeService] getNextNoticeOrder 실패:', error);
    return 1;
  }
}
